import { type TextFieldProps } from '@mui/material/TextField';
import { MuiTelInput, type MuiTelInputCountry, type MuiTelInputInfo, type MuiTelInputProps } from 'mui-tel-input';
import { type FC, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

// Calling codes libphonenumber can't resolve without more digits → primary country.
const AMBIGUOUS_CODE_MAP: Record<string, MuiTelInputCountry> = {
  '+1': 'US',
  '+44': 'GB',
  '+61': 'AU',
  '+7': 'RU',
  '+47': 'NO',
  '+212': 'MA',
  '+358': 'FI',
};

function inferCountry(value: string, fallback: MuiTelInputCountry): MuiTelInputCountry {
  if (!value) {
    return fallback;
  }
  for (const [code, country] of Object.entries(AMBIGUOUS_CODE_MAP)) {
    if (value.startsWith(code)) {
      return country;
    }
  }
  return fallback;
}

type TProps = Omit<TextFieldProps, 'name' | 'onChange' | 'value'> & {
  name: string;
  defaultCountry?: MuiTelInputProps['defaultCountry'];
};

export const RHFPhoneInput: FC<TProps> = ({ name, helperText, label, placeholder, defaultCountry = 'US', ...rest }) => {
  const { control } = useFormContext();
  const [selectedCountry, setSelectedCountry] = useState<MuiTelInputCountry>(defaultCountry ?? 'US');
  // Only remount (to sync flag) when the input is not focused to avoid losing caret position.
  const isFocused = useRef(false);
  const pendingCountry = useRef<MuiTelInputCountry | null>(null);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // Sync flag when field.value is set externally (e.g. reset()) without going through onChange.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (!field.value || isFocused.current) {
            return;
          }
          const inferred = inferCountry(field.value, selectedCountry);
          if (inferred !== selectedCountry) {
            setSelectedCountry(inferred);
          }
          // Only re-run when the value changes from outside (reset/setValue).
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [field.value]);

        return (
          <MuiTelInput
            key={selectedCountry}
            {...rest}
            value={field.value ?? ''}
            onChange={(value, info: MuiTelInputInfo) => {
              const resolved =
                (info.countryCode as MuiTelInputCountry | null) ??
                (value ? inferCountry(value, selectedCountry) : null);
              if (resolved && resolved !== selectedCountry) {
                if (isFocused.current) {
                  // defer remount until blur so typing isn't interrupted
                  pendingCountry.current = resolved;
                } else {
                  setSelectedCountry(resolved);
                }
              }
              field.onChange(value);
            }}
            onFocus={() => {
              isFocused.current = true;
            }}
            onBlur={() => {
              isFocused.current = false;
              field.onBlur();
              if (pendingCountry.current) {
                setSelectedCountry(pendingCountry.current);
                pendingCountry.current = null;
              }
            }}
            inputRef={field.ref}
            defaultCountry={selectedCountry}
            fullWidth
            label={label}
            placeholder={placeholder ?? '+1 555 123 4567'}
            error={!!error}
            helperText={error ? error.message : (helperText ?? 'Start with a country code (e.g. +1, +44, +880)')}
          />
        );
      }}
    />
  );
};
